import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { DataTable } from "primereact/datatable";
import { Row } from "primereact/row";
import {
	convertLongTimeToTotalMinutes,
	formatMinutes,
	localeDate,
} from "../../../../app/datetimeUtils";
import { CustomSortIcon } from "../../../../components/DatatableHelpers";
import { AdminReportsResponse } from "../adminReportsApiSlice";

// Required properties for the table
interface LessonUsageRequiredBase {
	mostRecentUse?: Date;
	timeSpent: string;
	totalMinutes: number;
}

export function LessonUsageByCategoryTable<T extends LessonUsageRequiredBase>(props: {
	className: string;
	isByCategory: boolean;
	fullData: AdminReportsResponse<T>;
	filteredRows: T[];
	hideRows: boolean;
	nameField: keyof T;
	nameHeader: string;
	dataKey: keyof T;
	footerLabel: string;
	onNameClick?: (rowData: T) => void;
}) {
	const hasData = props.filteredRows.length > 0;

	const recentUseTemplate = (rowData: T) => {
		return rowData.mostRecentUse ? localeDate(rowData.mostRecentUse) : "-";
	};

	const nameTemplate = (rowData: T) => {
		return (
			<a
				className={`${props.onNameClick ? "link" : ""}`}
				onClick={() => props.onNameClick?.(rowData)}>
				{String(rowData[props.nameField])}
			</a>
		);
	};

	const timeSpentTemplate = (rowData: T) => {
		return formatMinutes(rowData.totalMinutes);
	};

	const footerGroup = (
		<ColumnGroup>
			<Row>
				<Column
					footer={props.footerLabel}
					footerClassName="font-medium !text-right"
					colSpan={2}
				/>
				<Column
					footer={formatMinutes(
						convertLongTimeToTotalMinutes(props.fullData.totals.timeSpent)
					)}
					footerClassName="font-medium"
				/>
			</Row>
		</ColumnGroup>
	);

	return (
		<>
			<DataTable
				value={props.hideRows ? [] : props.filteredRows}
				dataKey={String(props.dataKey)}
				loading={props.fullData.rows == undefined}
				emptyMessage="No results found"
				pt={{
					// Targets the container of the "No results found" message for hiding it
					emptyMessage: {
						style: { display: !props.hideRows ? "table-row" : "none" },
					},
				}}
				stripedRows
				sortIcon={CustomSortIcon}
				className={`block tbl tbl-data tbl-datatable min-w-[40rem] cursor-default ${props.className}`}
				footerColumnGroup={hasData && footerGroup}>
				<Column
					header="Most Recent Use"
					body={recentUseTemplate}
					sortable
					sortField="mostRecentUse"></Column>
				<Column
					header={props.nameHeader}
					body={nameTemplate}
					sortable
					sortField={String(props.nameField)}></Column>
				<Column
					header="Total Time Spent"
					body={timeSpentTemplate}
					sortable
					sortField="totalMinutes"></Column>
			</DataTable>
			{props.fullData.rows.length !== props.filteredRows.length && (
				<p className="mt-2 mb-6 px-6 text-sm text-gray-700">
					Showing <span className="font-medium">{props.filteredRows.length}</span>{" "}
					{props.isByCategory
						? props.filteredRows.length === 1
							? "category"
							: "categories"
						: props.filteredRows.length === 1
							? "lesson"
							: "lessons"}
					<span>
						{" "}
						(filtered from{" "}
						<span className="font-medium">{props.fullData.rows.length}</span> total{" "}
						{props.isByCategory
							? props.fullData.rows.length === 1
								? "category"
								: "categories"
							: props.fullData.rows.length === 1
								? "lesson"
								: "lessons"}
						)
					</span>
				</p>
			)}
		</>
	);
}
